import { Component, OnInit, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient, HttpParams } from '@angular/common/http';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi } from 'ag-grid-community';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-foreign-colleges',
  standalone: true,
  templateUrl: './foreign-colleges.component.html',
  styleUrls: ['./foreign-colleges.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AgGridAngular,
  ],
})
export class ForeignCollegesComponent implements OnInit {
  adminName: string = 'Admin';
  adminImage: string = 'assets/admin.png';
  collegeSearchText: string = '';
  display = 'none';
  showAdd = false;
  editingId: number | null = null;
  deletingId: number | null = null;

  collegeForm!: FormGroup;
  countries: any[] = [];
  courses: any[] = [];
  colleges: any[] = [];
  selectedImage: string = '';
  previewImage: string | null = null;

  private gridApi!: GridApi;
  private originalCollegeData: any = null; // 🆕 store original row data

  public columnDefs: ColDef[] = [
    {
      headerName: 'S.No',
      valueGetter: (p) => p.node?.rowIndex! + 1,
      width: 80,
    },
    {
      field: 'country_name',
      headerName: 'Country',
      sortable: true,
      filter: true,
    },
    {
      field: 'course_name',
      headerName: 'Course',
      sortable: true,
      filter: true,
    },
    {
      field: 'college_name',
      headerName: 'College Name',
      sortable: true,
      filter: true,
    },
    { field: 'address', headerName: 'Address', sortable: true, filter: true },
    {
      field: 'description',
      headerName: 'Description',
      sortable: true,
      filter: true,
    },
    { field: 'hint', headerName: 'Hint', sortable: true, filter: true },
    {
      field: 'smscap_eligible',
      headerName: 'Eligible',
      valueFormatter: (p) => (p.value == 1 ? 'Yes' : 'No'),
      width: 100,
    },
    {
      headerName: 'Image',
      field: 'image',
      width: 120,
      cellRenderer: (params: any) => {
        if (!params.value) return '';
        const img = document.createElement('img');
        img.src = params.value;
        img.style.width = '40px';
        img.style.height = '40px';
        img.style.objectFit = 'cover';
        img.style.borderRadius = '6px';
        img.style.cursor = 'pointer';
        img.addEventListener('click', (ev) => {
          ev.stopPropagation();
          (window as any).ngForeignCollegesRef.openImagePreview(params.value);
        });
        return img;
      },
    },
    {
      headerName: 'Actions',
      width: 180,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.gap = '6px';
        container.style.alignItems = 'center';

        const comp = (window as any).ngForeignCollegesRef;

        // Edit button
        const editBtn = document.createElement('button');
        editBtn.className = 'btn btn-sm btn-outline-primary';
        editBtn.title = 'Edit College';
        editBtn.innerHTML = `<i class="fa fa-edit"></i>`;
        editBtn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          comp.zone.run(() => comp.openEdit(params.data));
        });

        // Delete button
        const delBtn = document.createElement('button');
        delBtn.className = 'btn btn-sm btn-outline-danger';
        delBtn.title = 'Delete College';
        delBtn.innerHTML = `<i class="fa fa-trash"></i>`;
        delBtn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          comp.zone.run(() => comp.deleteCollege(params.data));
        });

        container.appendChild(editBtn);
        container.appendChild(delBtn);
        return container;
      },
    },
  ];

  public defaultColDef: ColDef = { resizable: true, flex: 1, minWidth: 120 };

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public zone: NgZone
  ) {}

  ngOnInit(): void {
    (window as any).ngForeignCollegesRef = this;
    this.buildForm();
    this.loadCountries();
    this.loadColleges();
  }

  buildForm() {
    this.collegeForm = this.fb.group({
      country_id: ['', Validators.required],
      foreign_course_id: ['', Validators.required],
      college_name: ['', Validators.required],
      description: ['', Validators.required],
      address: ['', Validators.required],
      hint: [''],
      smscap_eligible: [1, Validators.required],
    });
  }

  loadCountries() {
    this.http.get<any>('https://smscap.in/api/country').subscribe({
      next: (res) => (this.countries = res?.data || []),
      error: (err) => (this.countries = []),
    });
  }

  onCountryChange(countryId: any) {
    if (!countryId) return (this.courses = []);
    this.http
      .get<any>('https://smscap.in/api/foreign_course/' + countryId)
      .subscribe({
        next: (res) => (this.courses = res?.data || []),
        error: () => (this.courses = []),
      });
  }

  loadColleges() {
    this.http.get<any>('https://smscap.in/api/foreign_college').subscribe({
      next: (res) => {
        this.colleges = (res?.data || []).map((clg: any) => ({
          ...clg,
          image: clg.image ? `https://smscap.in/${clg.image}` : null,
        }));
      },
      error: (err) => {
        console.error('loadColleges error', err);
        this.colleges = [];
      },
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => (this.selectedImage = reader.result as string);
      reader.readAsDataURL(file);
    }
  }

  openAdd() {
    this.display = 'block';
    this.showAdd = true;
    this.editingId = null;
    this.originalCollegeData = null;
    this.collegeForm.reset({ smscap_eligible: 1 });
    this.selectedImage = '';
  }

  openEdit(row: any) {
    if (!row) return;
    this.editingId = row.id;
    this.originalCollegeData = { ...row }; // 🆕 store original data

    this.collegeForm.patchValue({
      country_id: row.country_id ?? '',
      foreign_course_id: row.foreign_course_id ?? '',
      college_name: row.college_name ?? '',
      description: row.description ?? '',
      address: row.address ?? '',
      hint: row.hint ?? '',
      smscap_eligible:
        typeof row.smscap_eligible !== 'undefined' ? row.smscap_eligible : 1,
    });

    this.selectedImage = row.image || '';
    this.display = 'block';
    this.showAdd = true;

    if (row.country_id) this.onCountryChange(row.country_id);
  }

  onCloseHandled() {
    this.display = 'none';
    this.collegeForm.reset({ smscap_eligible: 1 });
    this.selectedImage = '';
    this.editingId = null;
    this.originalCollegeData = null;
  }

  addCollege() {
    if (this.collegeForm.invalid) {
      this.collegeForm.markAllAsTouched();
      return;
    }

    const formValue = this.collegeForm.value;
    let payload: any = {};

    if (this.editingId) {
      // 🆕 Differential update (only changed fields)
      payload.id = this.editingId;

      const currentValues = { ...formValue, image: this.selectedImage };
      const original = this.originalCollegeData || {};

      Object.keys(currentValues).forEach((key) => {
        if (currentValues[key] !== original[key]) {
          payload[key] = currentValues[key];
        }
      });

      // If nothing changed, skip update
      if (Object.keys(payload).length === 1) {
        window.alert('No changes detected.');
        return;
      }
    } else {
      // Full payload for new record
      payload = { ...formValue, image: this.selectedImage };
    }

    this.http.post('https://smscap.in/api/foreign_college', payload).subscribe({
      next: () => {
        this.loadColleges();
        this.onCloseHandled();
      },
      error: (err) => {
        console.error('addCollege error', err);
        window.alert('Failed to save college.');
      },
    });
  }

  openImagePreview(imageUrl: string) {
    this.previewImage = imageUrl;
  }

  closeImagePreview() {
    this.previewImage = null;
  }

  onCollegeGridReady(params: any) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  onCollegeFilterChanged() {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', this.collegeSearchText);
    }
  }

  clearCollegeSearch() {
    this.collegeSearchText = '';
    this.onCollegeFilterChanged();
  }

  deleteCollege(row: any) {
    if (!row) return;
    const id = row.id ?? row.college_id ?? row.collegeId;
    if (!id) return;

    const confirmDelete = window.confirm(
      `Delete "${
        row.college_name || 'this college'
      }"? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    this.deletingId = id;

    // ✅ Immediately remove from grid view
    this.colleges = this.colleges.filter(
      (c) => String(c.id ?? c.college_id ?? c.collegeId) !== String(id)
    );

    // ✅ Refresh grid immediately to reflect the removal
    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.colleges);
      try {
        this.gridApi.refreshCells();
      } catch (e) {}
    }

    // ✅ Call backend delete API
    const params = new HttpParams().set('id', String(id));
    this.http
      .delete('https://smscap.in/api/foreign_college', { params })
      .subscribe({
        next: () => {
          console.log('College deleted successfully');
          this.deletingId = null;
        },
        error: (err) => {
          console.error('Delete failed:', err);
          window.alert('Failed to delete college. Refreshing list...');
          // restore full list from DB if deletion failed
          this.loadColleges();
          this.deletingId = null;
        },
      });
  }
}
